import { useState, useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isCommentNode } from '../nodes/CommentNode';
import type { CommentData, CommentReply } from '../nodes/CommentNode';
import { X, MessageSquare, CheckCircle, Clock, CircleCheck, EllipsisVertical } from 'lucide-react';

interface CommentPanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: string;
  onSave?: () => void;
}

interface CommentWithPosition extends CommentData {
  position: number;
}

export default function CommentPanel({ isOpen, onClose, currentUser, onSave }: CommentPanelProps) {
  const [editor] = useLexicalComposerContext();
  const [comments, setComments] = useState<CommentWithPosition[]>([]);

  // Extract comments from editor state
  useEffect(() => {
    if (!isOpen) return;

    const updateComments = () => {
      editor.getEditorState().read(() => {
        const extractedComments: CommentWithPosition[] = [];
        let position = 0;

        const seenGroups = new Set<string>();
        
        const visitNode = (node: any, depth = 0) => {
          if ($isCommentNode(node)) {
            const commentData = node.getCommentData();
            const groupId = commentData.groupId || commentData.id;
            
            // Only add if we haven't seen this group yet
            if (!seenGroups.has(groupId)) {
              seenGroups.add(groupId);
              extractedComments.push({
                ...commentData,
                position: position++,
              });
            }
          }

          const children = node.getChildren?.();
          if (children) {
            for (const child of children) {
              visitNode(child, depth + 1);
            }
          }
        };

        const root = editor.getEditorState()._nodeMap.get('root');
        if (root) {
          visitNode(root);
        }

        setComments(extractedComments.sort((a, b) => a.position - b.position));
      });
    };

    updateComments();

    // Listen for editor state changes
    const unregister = editor.registerUpdateListener(() => {
      updateComments();
    });

    return unregister;
  }, [editor, isOpen]);

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = (now.getTime() - date.getTime()) / (1000 * 60);
    const diffInHours = diffInMinutes / 60;

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${Math.floor(diffInMinutes)}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };

  const resolvedComments = comments.filter(c => c.resolved);
  const activeComments = comments.filter(c => !c.resolved);

  if (!isOpen) return null;

  return (
    <div className="sidebar sticky top-32 col-span-4 h-[88vh] overflow-y-scroll border-l p-4 pb-20 dark:border-gray-800 dark:bg-gray-950">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Document Activity</h3>
          <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full dark:bg-gray-700 dark:text-gray-300">
            {activeComments.length}
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      {comments.length === 0 ? (
        <div className="text-center text-gray-500 dark:text-gray-400 mt-8">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <p className="text-sm">No comments yet</p>
          <p className="text-xs mt-1">Select text to add your first comment</p>
        </div>
      ) : (
        <div>
          {/* Active Comments */}
          {activeComments.length > 0 && (
            <>
              <div className="mb-4 pb-2 border-b border-orange-200 dark:border-orange-800">
                <h4 className="text-sm font-medium text-orange-800 dark:text-orange-400 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Active ({activeComments.length})
                </h4>
              </div>
              {activeComments.map((comment) => (
                <CommentItem key={comment.id} comment={comment} formatTimestamp={formatTimestamp} currentUser={currentUser} onSave={onSave} />
              ))}
            </>
          )}

          {/* Resolved Comments */}
          {resolvedComments.length > 0 && (
            <>
              <div className="mb-4 pb-2 border-b border-green-200 dark:border-green-800 mt-6">
                <h4 className="text-sm font-medium text-green-800 dark:text-green-400 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Resolved ({resolvedComments.length})
                </h4>
              </div>
              {resolvedComments.map((comment) => (
                <CommentItem key={comment.id} comment={comment} formatTimestamp={formatTimestamp} currentUser={currentUser} onSave={onSave} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface CommentItemProps {
  comment: CommentWithPosition;
  formatTimestamp: (timestamp: number) => string;
  currentUser: string;
  onSave?: () => void;
}


function CommentItem({ comment, formatTimestamp, currentUser, onSave }: CommentItemProps) {
  const [editor] = useLexicalComposerContext();
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (activeMenuId) {
        setActiveMenuId(null);
      }
    };

    if (activeMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeMenuId]);

  const handleCommentClick = () => {
    // Find the comment node with matching ID
    const findCommentNodeById = (commentId: string) => {
      const nodeMap = editor.getEditorState()._nodeMap;
      for (const [, node] of nodeMap) {
        if ($isCommentNode(node) && node.getCommentData().id === commentId) {
          return node;
        }
      }
      return null;
    };

    // Just scroll to comment - no cursor movement or focus
    const scrollToComment = (retryCount = 0) => {
      editor.getEditorState().read(() => {
        const commentNode = findCommentNodeById(comment.id);
        if (commentNode) {
          // Get the DOM element for this comment node
          const domElement = editor.getElementByKey(commentNode.getKey());
          if (domElement) {
            // Scroll the element into view smoothly
            domElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });

            // Optional: Add a temporary highlight effect
            domElement.style.boxShadow = '0 0 0 3px rgba(255, 152, 0, 0.5)';
            setTimeout(() => {
              domElement.style.boxShadow = '';
            }, 2000);
          } else if (retryCount < 3) {
            // If DOM element isn't ready, retry after a short delay
            setTimeout(() => scrollToComment(retryCount + 1), 10);
          }
        }
      });
    };

    scrollToComment();
  };

  const handleResolve = (e: React.MouseEvent) => {
    e.stopPropagation();
    editor.update(() => {
      const findCommentNodeById = (commentId: string) => {
        const nodeMap = editor.getEditorState()._nodeMap;
        for (const [, node] of nodeMap) {
          if ($isCommentNode(node) && node.getCommentData().id === commentId) {
            return node;
          }
        }
        return null;
      };

      const commentNode = findCommentNodeById(comment.id);
      if (commentNode) {
        const updatedData = {
          ...commentNode.getCommentData(),
          resolved: true,
          timestamp: Date.now()
        };
        commentNode.setCommentData(updatedData);
      }
    }, { onUpdate: onSave, discrete: true });
  };

  const handleReply = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowReplyInput(!showReplyInput);
  };

  const handleSubmitReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    editor.update(() => {
      const findCommentNodeById = (commentId: string) => {
        const nodeMap = editor.getEditorState()._nodeMap;
        for (const [, node] of nodeMap) {
          if ($isCommentNode(node) && node.getCommentData().id === commentId) {
            return node;
          }
        }
        return null;
      };

      const commentNode = findCommentNodeById(comment.id);
      if (commentNode) {
        const newReply: CommentReply = {
          id: Date.now().toString(),
          content: replyContent,
          author: currentUser,
          timestamp: Date.now()
        };

        const updatedData = {
          ...commentNode.getCommentData(),
          replies: [...(commentNode.getCommentData().replies || []), newReply]
        };
        commentNode.setCommentData(updatedData);
      }
    }, { onUpdate: onSave, discrete: true });

    setReplyContent('');
    setShowReplyInput(false);
  };

  const handleContextMenu = (commentId: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveMenuId(activeMenuId === commentId ? null : commentId);
  };

  const handleEditComment = (commentToEdit: CommentData | CommentReply) => () => {
    // Skipping permission checks until collaborative editing is implemented
    setEditingCommentId(commentToEdit.id);
    setEditContent(commentToEdit.content);
    setActiveMenuId(null);
  };

  const handleSaveEdit = () => {
    if (!editContent.trim()) return;

    editor.update(() => {
      const findCommentNodeById = (commentId: string) => {
        const nodeMap = editor.getEditorState()._nodeMap;
        for (const [, node] of nodeMap) {
          if ($isCommentNode(node) && node.getCommentData().id === commentId) {
            return node;
          }
        }
        return null;
      };

      const commentNode = findCommentNodeById(comment.id);
      if (commentNode) {
        const currentData = commentNode.getCommentData();
        
        // If editing the main comment
        if (editingCommentId === comment.id) {
          const updatedData = {
            ...currentData,
            content: editContent.trim(),
            timestamp: Date.now()
          };
          commentNode.setCommentData(updatedData);
        } else {
          // Editing a reply
          const updatedReplies = currentData.replies?.map(reply => 
            reply.id === editingCommentId 
              ? { ...reply, content: editContent.trim(), timestamp: Date.now() }
              : reply
          ) || [];
          
          commentNode.setCommentData({
            ...currentData,
            replies: updatedReplies
          });
        }
      }
    }, { onUpdate: onSave, discrete: true });

    setEditingCommentId(null);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditContent('');
  };

  const handleDeleteComment = (commentToDelete: CommentData | CommentReply) => () => {
    // Skipping permission checks until collaborative editing is implemented
    editor.update(() => {
      const findCommentNodeById = (commentId: string) => {
        const nodeMap = editor.getEditorState()._nodeMap;
        for (const [, node] of nodeMap) {
          if ($isCommentNode(node) && node.getCommentData().id === commentId) {
            return node;
          }
        }
        return null;
      };

      const commentNode = findCommentNodeById(comment.id);
      if (!commentNode) return;

      // If deleting the main comment, remove the entire comment node
      if (commentToDelete.id === comment.id) {
        // Get all child nodes (the text content)
        const children = commentNode.getChildren();

        // Insert children before the comment node
        for (const child of children) {
          commentNode.insertBefore(child);
        }

        // Now remove the empty comment node
        commentNode.remove();
      } else {
        // Deleting a reply - remove it from the replies array
        const currentData = commentNode.getCommentData();
        const updatedReplies = currentData.replies?.filter(reply => reply.id !== commentToDelete.id) || [];
        
        commentNode.setCommentData({
          ...currentData,
          replies: updatedReplies
        });
      }
    }, { onUpdate: onSave, discrete: true });
    setActiveMenuId(null);
  };

  // Create a flat array of all comments in the thread
  const allComments = [comment, ...(comment.replies || [])];

  return (
    <div className={`mb-4 space-y-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${comment.resolved ? 'opacity-60' : ''}`}>
      {allComments.map((commentData, index) => {
        const isOriginal = index === 0;
        const isFirst = index === 0;
        const isLast = index === allComments.length - 1;

        return (
          <div
            key={commentData.id}
            onClick={isOriginal ? handleCommentClick : undefined}
            className={`p-4 ${isLast ? '' : 'border-b'} ${isFirst && !comment.resolved ? 'cursor-pointer' : ''}`}
          >
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium bg-black flex-shrink-0">
                {commentData.author.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{commentData.author}</span>
                      {comment.resolved && isOriginal && (
                        <CheckCircle className="w-3 h-3 text-green-500" />
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {formatTimestamp(commentData.timestamp)}
                    </div>
                  </div>
                  <div className="flex items-start gap-1 ml-3">
                    {!comment.resolved && isOriginal && (
                      <button
                        onClick={handleResolve}
                        className="flex items-center justify-center w-6 h-6 text-black hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                        title="Resolve comment"
                      >
                        <CircleCheck className="w-4 h-4" />
                      </button>
                    )}
                    {!comment.resolved && (
                      <div className="relative">
                        <button
                          onClick={handleContextMenu(commentData.id)}
                          className="flex items-center justify-center w-6 h-6 text-black hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                          title="More options"
                        >
                          <EllipsisVertical className="w-4 h-4" />
                        </button>
                        {activeMenuId === commentData.id && (
                          <div className="absolute right-0 top-full mt-1 w-32 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
                            <button
                              onClick={handleEditComment(commentData)}
                              className="w-full px-3 py-2 text-xs text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-md"
                            >
                              Edit
                            </button>
                            <button
                              onClick={handleDeleteComment(commentData)}
                              className="w-full px-3 py-2 text-xs text-left text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 last:rounded-b-md"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-end">
                  {editingCommentId === commentData.id ? (
                    <div className="flex-1 mr-3">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                        rows={3}
                        autoFocus
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={handleSaveEdit}
                          disabled={!editContent.trim()}
                          className="px-2 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded transition-colors"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed flex-1 mr-3">{commentData.content}</p>
                      {isLast && !comment.resolved && !showReplyInput && (
                        <button
                          onClick={handleReply}
                          className="px-4 py-2 text-xs text-white bg-black hover:bg-gray-800 rounded transition-colors flex-shrink-0"
                          title="Reply to comment"
                        >
                          Reply
                        </button>
                      )}
                    </>
                  )}
                </div>

              </div>
            </div>
          </div>
        );
      })}

      {showReplyInput && !comment.resolved && (
        <div className="p-4 border-b">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium bg-black flex-shrink-0">
              {currentUser.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{currentUser}</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Now
                  </div>
                </div>
              </div>
              <form onSubmit={handleSubmitReply} className="space-y-3">
                <textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Write a reply..."
                  className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                  rows={3}
                  autoFocus
                />
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowReplyInput(false);
                      setReplyContent('');
                    }}
                    className="px-4 py-2 text-xs text-white bg-black hover:bg-gray-800 rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!replyContent.trim()}
                    className="px-4 py-2 text-xs text-white bg-black hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed rounded transition-colors"
                  >
                    Reply
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}